/*
 * TurnPane — full-pane shell for a single active turn (pane mode).
 *
 * The pane fills the chat stage center column. Layout:
 *
 *   ┌── eyebrow (section · index/total) ──────────────────┐
 *   │  B · STACK & TOOLS               03 of 12           │
 *   │                                                      │
 *   │  [pane body — turn content slot]                     │
 *   │                                                      │
 *   └──────────────────────────────────────────────────────┘
 *
 * Used by OrbitIntake's pane shell to wrap whichever turn is active
 * (orbit chat-turn, batch_confirm card, problem_canvas, prd_review,
 * etc.). The eyebrow is hidden when no section tag is provided so
 * directive-card turns (PRD review, problem canvas) render flush.
 *
 * Visual variants via the `variant` prop:
 *   - 'orbit'   — narration + chips/composer
 *   - 'user'    — past user answer, dim/right-aligned
 *   - 'system'  — system note, single line center
 *   - 'card'    — full-bleed card body (directive cards)
 *
 * Brand: light card surface #eef3ff, 10px radius, no card border,
 * Plex Mono caps eyebrow in brand blue. Past-turn variant gets a
 * subtle 0.62 opacity + "tap to come back" CTA in the corner.
 */

import React from 'react';

export type TurnPaneVariant = 'orbit' | 'user' | 'system' | 'card';


export interface TurnPaneProps {
  index: number;
  total: number;
  sectionTag?: string | null;
  variant: TurnPaneVariant;
  isLatest: boolean;
  /** When true, the pane fades back to opacity 0 then 1 — used for
   *  the swipe-between-turns transition. */
  transitionKey?: string | number;
  /** When true, the pane breaks out of the 720px reading column to the
   *  full stage chrome width (1080px) and drops its turn-counter meta —
   *  used by the landing route chooser so the three cards get room to
   *  grow in width, not height. */
  wide?: boolean;
  children: React.ReactNode;
}


export const TurnPane: React.FC<TurnPaneProps> = ({
  index,
  total,
  sectionTag,
  variant,
  isLatest,
  transitionKey,
  wide,
  children,
}) => {
  const classes = [
    'aa-turn-pane',
    `aa-turn-pane--${variant}`,
    isLatest ? 'aa-turn-pane--latest' : 'aa-turn-pane--past',
    wide ? 'aa-turn-pane--wide' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} data-pane-key={transitionKey}>
      {!wide && (sectionTag || total > 0) && (
        <header className="aa-turn-pane-meta">
          {sectionTag && (
            <span className="aa-turn-pane-section">{sectionTag}</span>
          )}
          <span className="aa-turn-pane-counter" aria-label={`Turn ${index + 1} of ${total}`}>
            {String(index + 1).padStart(2, '0')}
            <span className="aa-turn-pane-counter-sep">/</span>
            {String(total).padStart(2, '0')}
          </span>
        </header>
      )}
      <div className="aa-turn-pane-body">{children}</div>
    </div>
  );
};

export default TurnPane;
